import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

const isSentryDisabled =
  import.meta.env.SENTRY_DISABLED === 'true' ||
  import.meta.env.SENTRY_DISABLED === true;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ApiErrorOptions {
  defaultMessage?: string;
  showToast?: boolean;
  toastMessage?: string;
}

export const handleApiError = (
  error: unknown,
  options: ApiErrorOptions = {}
): string => {
  const {
    defaultMessage = 'Request failed',
    showToast = true,
    toastMessage,
  } = options;

  let errorMessage = defaultMessage;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;
    errorMessage =
      axiosError.response?.data?.message || axiosError.message || errorMessage;

    console.error(
      `${defaultMessage} (AxiosError):`,
      axiosError.response?.data || axiosError.message
    );
    if (axiosError.response?.status !== 401 && !isSentryDisabled) {
      Sentry.captureException(axiosError, { extra: { defaultMessage } });
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
    console.error(`${defaultMessage} (Error):`, error.message);
    if (!isSentryDisabled) {
      Sentry.captureException(error, { extra: { defaultMessage } });
    }
  } else {
    console.error(`${defaultMessage} (Unknown error):`, error);
    if (!isSentryDisabled) {
      Sentry.captureException(new Error(defaultMessage), {
        extra: { defaultMessage, originalError: error },
      });
    }
  }

  if (showToast) {
    toast.error(toastMessage || `Error: ${errorMessage}`);
  }

  return errorMessage;
};

export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}.${month}.${day}.`;
  } catch (error) {
    console.error('Invalid date format:', error);
    return dateString;
  }
};

export const isDevMode = () => import.meta.env.DEVELOPMENT === 'true';
